import {
  ChartOptionElement,
  useGetChartOptionLazyQuery,
  useRemoveChartOptionMutation,
  useSetChartOptionMutation,
  useResetChartOptionsMutation,
} from 'gqlTypes'
import { useContext, useMemo, useCallback, useEffect, useState } from 'react'
import pick from 'lodash/pick'

import {
  ChartOptionsContext,
  ChartOptionsValue,
} from 'contexts/ChartOptionsContext'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'
import { chartOptionAtom, keyForElementIdAndType } from 'recoil/settings'

const useChartOptions = (
  id: string,
  type: ChartOptionElement,
  onChange: (value: ChartOptionsValue) => any = () => {},
  reportId?: string
) => {
  const atom = chartOptionAtom(keyForElementIdAndType({ id, type, reportId }))
  const baseValues = useRecoilValue(atom)
  const mergedValues = useContext(ChartOptionsContext)

  const [updateOptions, { loading }] = useSetChartOptionMutation()
  const [removeOption, removeData] = useRemoveChartOptionMutation({
    variables: { id: id, type: type },
  })
  const [getChartOption, { data, ...rest }] = useGetChartOptionLazyQuery({
    fetchPolicy: 'network-only',
  })
  const [resetChartOptions, resetData] = useResetChartOptionsMutation({
    variables: { id: id },
  })

  const setValue = useSetRecoilState(atom)

  const handleChange = useRecoilCallback(
    ({ set, snapshot }) =>
      (value: ChartOptionsValue) => {
        const currentValues = snapshot.getLoadable(atom).getValue()
        let newValue = JSON.parse(
          JSON.stringify({ ...mergedValues, ...currentValues, ...value })
        )
        if (
          !newValue.pastResults &&
          newValue.config &&
          newValue.config.hidden
        ) {
          newValue.config.hidden['Past Results'] = []
        }
        set(atom, newValue)
        updateOptions({
          variables: { id, type, input: newValue },
          fetchPolicy: 'network-only',
        }).then((rslt) => {
          // need refetch or favorites and full report get out of sync
          rest.refetch()
        })
      },
    [onChange, updateOptions, id, type, atom]
  )

  const handleUseDefault = () => {
    removeOption().then((rslt) => {
      rest.refetch().then((data) => {
        setValue(data.data.chartOption || {})
      })
    })
  }

  const handleGlobalReset = useRecoilCallback(
    ({ reset, refresh, snapshot }) =>
      (reportId: string) => {
        const release = snapshot.retain()
        resetChartOptions().then((rslt) => {
          const nodes = snapshot.getNodes_UNSTABLE()
          try {
            Array.from(nodes).forEach((node) => {
              if (node.key.match(new RegExp(`REPORT:${reportId}::.*?`))) {
                const nodeAtom = chartOptionAtom(
                  node.key.replace(/chartOption__"(.*?)"/, '$1')
                )
                reset(nodeAtom)
              }
            })
            rest.refetch()
          } finally {
            release()
          }
        })
      },
    [resetChartOptions, rest]
  )

  useEffect(() => {
    getChartOption({ variables: { id, type } }).then((data) => {
      const chartOption = data?.data?.chartOption
      if (chartOption)
        setValue(
          pick(chartOption, ['cohort', 'subgroup', 'pastResults', 'config'])
        )
    })
  }, [])

  const saveOptions = useCallback(
    () => updateOptions({ variables: { id, type, input: mergedValues } }),
    [updateOptions, mergedValues, id, type]
  )

  const handlers = useMemo(() => {
    const onChangeCohort = (cohort: string) => handleChange({ cohort })
    const onChangeSubgroup = (subgroup: string) => handleChange({ subgroup })
    const onChangePastResults = (pastResults: boolean | null) =>
      handleChange({ pastResults })
    const onChangeConfig = (config: any) => handleChange({ config })
    const onChangeValue = (input: ChartOptionsValue) => handleChange(input)

    return {
      onChangeCohort,
      onChangeSubgroup,
      onChangePastResults,
      onChangeConfig,
      onChangeValue,
      handleUseDefault,
      handleGlobalReset,
    }
  }, [handleChange, handleUseDefault, handleGlobalReset])

  return useMemo(
    () => ({
      ...mergedValues,
      baseValues,
      ...handlers,
      saveOptions,
      saving: loading,
    }),
    [
      mergedValues,
      baseValues,
      baseValues.config,
      handlers,
      saveOptions,
      loading,
    ]
  )
}

export default useChartOptions
